import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import PDFViewer from "@/components/PDFViewer";
import Navigation from "@/components/Navigation";
import { PremiumUnlockGate } from "@/components/PremiumUnlockGate";
import type { User } from "@supabase/supabase-js";
import { useUnlock } from "@/hooks/useUnlock";
import { usePremiumProgressTracker } from "@/hooks/usePremiumProgressTracker";

interface DigitalProduct {
  id: string;
  title: string;
  description: string;
  category: string;
  file_url: string | null;
  pdf_file_path: string | null;
  content_type: string;
  preview_image: string | null;
}

const ResourceViewer = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<DigitalProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const { isUnlocked } = useUnlock(user, "digital_resource", product?.id ?? null);

  usePremiumProgressTracker({
    user,
    enabled: isUnlocked && !!product?.id,
    contentType: "digital_resource",
    entityId: product?.id ?? null,
    title: product?.title,
    featuredImage: product?.preview_image,
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setUser(s?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const { data, error } = await supabase
        .from("digital_products")
        .select("*")
        .eq("id", productId)
        .eq("is_active", true)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        toast({
          title: "Product Not Found",
          description: "This resource doesn't exist or is no longer available.",
          variant: "destructive",
        });
        navigate("/premium-resources");
        return;
      }

      if (data.content_type !== "viewable" || !data.pdf_file_path) {
        toast({
          title: "Not Viewable",
          description: "This resource is download-only.",
        });
        navigate("/premium-resources");
        return;
      }

      setProduct(data);

      // Get public URL for the PDF
      const { data: urlData } = supabase.storage
        .from("product-pdfs")
        .getPublicUrl(data.pdf_file_path);

      if (urlData?.publicUrl) {
        setPdfUrl(urlData.publicUrl);
      }

      // Track view
      await supabase.from("product_downloads").insert({
        product_id: data.id,
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      console.error("Error fetching product:", error);
      toast({
        title: "Error",
        description: "Failed to load resource.",
        variant: "destructive",
      });
      navigate("/premium-resources");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-[600px] w-full" />
        </div>
      </div>
    );
  }

  if (!product || !pdfUrl) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{product.title} - View | RunoBlog</title>
        <meta name="description" content={product.description} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-card/95 backdrop-blur border-b border-border">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/premium-resources">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Link>
              </Button>
              <div className="hidden md:block">
                <h1 className="font-semibold text-lg line-clamp-1">{product.title}</h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {product.file_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={product.file_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Source
                  </a>
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Mobile Title */}
        <div className="md:hidden p-4 bg-card border-b border-border">
          <h1 className="font-semibold">{product.title}</h1>
          <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
        </div>

        {/* PDF Viewer (gated) */}
        <div className="flex-1">
          <PremiumUnlockGate
            purchaseType="digital_resource"
            entityId={product.id}
            title={product.title}
            description="Unlock this resource to read it in your browser, or get All-Access for everything on RunoBlog."
          >
            <PDFViewer fileUrl={pdfUrl} title={product.title} />
          </PremiumUnlockGate>
        </div>
      </div>
    </>
  );
};

export default ResourceViewer;